import { createApi } from "@reduxjs/toolkit/query/react";

import { logout, setUser, setUserToken, setChallenge, clearChallenge } from "./auth-slice";
import {
  baseQuery,
  BOOK_A_DEMO_MUTATION,
  FORGET_MUTATION,
  GET_CURRENT_USER,
  LOGIN_MUTATION,
  RESET_PASSWORD_MUTATION,
  UPDATE_USER_PASSWORD,
  VERIFY_LOGIN_CODE_MUTATION,
  RESEND_VERIFICATION_CODE_MUTATION,
  REFRESH_SESSION_MUTATION,
} from "@/graphql";
import { signedUrlApi } from "../signed-url";

interface UserReponse {
  getLoggedInUser: {
    data: IUser;
  };
}

interface LoginReponse {
  login: {
    data: AccessToken;
  };
}

interface VerifyCodeResponse {
  verifyLoginCode: {
    data: AccessToken;
  };
}

interface ResendCodeData {
  challenge: string;
  challengeSession: string;
}

interface ResendCodeResponse {
  resendVerificationCode: {
    data: ResendCodeData;
  };
}

interface RefreshSessionResponse {
  refreshSession: {
    data: {
      accessToken: string;
    };
  };
}

export const authApi = createApi({
  reducerPath: "api/auth",
  baseQuery,
  tagTypes: ["CurrentUser"],
  endpoints: (builder) => ({
    getUserFromToken: builder.query<IUser, void>({
      query: () => ({
        document: GET_CURRENT_USER,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data: user } = await queryFulfilled;

          let profileImageUrl = "";

          if (user.profileImage) {
            const { signedUrl } = await dispatch(
              signedUrlApi.endpoints.getAccessSignedUrl.initiate({
                fileName: user.profileImage,
              })
            ).unwrap();

            profileImageUrl = signedUrl;
          }

          dispatch(setUser({ ...user, profileImage: profileImageUrl }));
        } catch (error) {
          dispatch(logout());
        }
      },
      providesTags: ["CurrentUser"],
      transformResponse: (response: UserReponse) =>
        response.getLoggedInUser.data,
    }),
    login: builder.mutation<
      AccessToken,
      { email: string; password: string; rememberDevice?: boolean }
    >({
      query: ({ email, password, rememberDevice }) => ({
        document: LOGIN_MUTATION,
        variables: { email, password, rememberDevice },
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;

          if (data.challenge === "EMAIL_VERIFICATION" && data.challengeSession) {
            // 2FA required — store challenge state
            dispatch(
              setChallenge({
                challengeSession: data.challengeSession,
              })
            );
          } else if (data.accessToken) {
            dispatch(setUserToken(data.accessToken));
          } else if (data.challenge === "EMAIL_VERIFICATION") {
            // challengeSession missing — server error
            console.error("EMAIL_VERIFICATION challenge received without challengeSession");
          }
        } catch {
          // Login failed — don't logout, as it would destroy any existing session
        }
      },
      transformResponse: (response: LoginReponse) => response.login.data,
    }),
    verifyLoginCode: builder.mutation<
      AccessToken,
      { code: string; challengeSession: string; rememberDevice?: boolean }
    >({
      query: ({ code, challengeSession, rememberDevice }) => ({
        document: VERIFY_LOGIN_CODE_MUTATION,
        variables: { code, challengeSession, rememberDevice },
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.accessToken) {
            dispatch(setUserToken(data.accessToken));
            dispatch(clearChallenge());
          }
        } catch (error) {
          // Don't logout on failed code — let user retry
        }
      },
      transformResponse: (response: VerifyCodeResponse) =>
        response.verifyLoginCode.data,
    }),
    resendVerificationCode: builder.mutation<
      ResendCodeData,
      { challengeSession: string }
    >({
      query: ({ challengeSession }) => ({
        document: RESEND_VERIFICATION_CODE_MUTATION,
        variables: { challengeSession },
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.challengeSession) {
            dispatch(
              setChallenge({
                challengeSession: data.challengeSession,
              })
            );
          }
        } catch {
          // Don't logout on resend failure
        }
      },
      transformResponse: (response: ResendCodeResponse) =>
        response.resendVerificationCode.data,
    }),
    refreshSession: builder.mutation<string, { email: string }>({
      query: ({ email }) => ({
        document: REFRESH_SESSION_MUTATION,
        variables: { email },
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data: accessToken } = await queryFulfilled;
          dispatch(setUserToken(accessToken));
        } catch (error: unknown) {
          // Only logout if the refresh token is invalid/expired (auth error).
          // Transient network errors should NOT destroy the session — the
          // existing access token may still be valid, and the next refresh
          // cycle (or next API call triggering a 401) will handle it.

          // RTK Query's queryFulfilled rejects with { error: { status, data }, meta },
          // not a plain Error. Extract the message from the GraphQL error response.
          let message = "";
          if (error instanceof Error) {
            message = error.message;
          } else {
            const rtkError = error as { error?: { status?: number; data?: { errors?: { message?: string }[] } } };
            const gqlMessage = rtkError?.error?.data?.errors?.[0]?.message ?? "";
            const status = rtkError?.error?.status;
            message = gqlMessage || (status ? String(status) : "");
          }

          const isAuthError =
            /token.*(?:expired|invalid|revoked)/i.test(message) ||
            /not authorized/i.test(message) ||
            message === "401";
          if (isAuthError) {
            dispatch(logout());
          }
        }
      },
      transformResponse: (response: RefreshSessionResponse) =>
        response.refreshSession.data.accessToken,
    }),
    resetPassword: builder.mutation<
      any,
      { newPassword: string; token: string }
    >({
      query: ({ newPassword, token }) => ({
        document: RESET_PASSWORD_MUTATION,
        variables: { newPassword, token },
      }),
    }),
    forgetPassword: builder.mutation<any, { email: string }>({
      query: ({ email }) => ({
        document: FORGET_MUTATION,
        variables: { email },
      }),
    }),
    bookADemo: builder.mutation<any, { name: string, email: string, companyName: string, firmType: string }>({
      query: ({ name, email, companyName, firmType }) => ({
        document: BOOK_A_DEMO_MUTATION,
        variables: { name, email, companyName, firmType },
      }),
    }),
    updateUserPassword: builder.mutation<any, { email: string, newPassword: string, previousPassword: string, newUser: boolean }>({
      query: ({ email, newPassword, newUser, previousPassword }) => ({
        document: UPDATE_USER_PASSWORD,
        variables: { email, newPassword, newUser, previousPassword },
      }),
    }),
  }),
});

export const {
  useGetUserFromTokenQuery,
  useLoginMutation,
  useVerifyLoginCodeMutation,
  useResendVerificationCodeMutation,
  useRefreshSessionMutation,
  useResetPasswordMutation,
  useForgetPasswordMutation,
  useUpdateUserPasswordMutation,
  useBookADemoMutation,
} = authApi;
